import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Box,
  Button,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import IdleTimeoutService from '../../services/idleTimeoutService';

const IdleTimeoutModule = () => {
  const [settings, setSettings] = useState({ enabled: true, duration: 5 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tempDuration, setTempDuration] = useState(5);

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const fetchedSettings = await IdleTimeoutService.getSettings();
        setSettings(fetchedSettings);
        setTempDuration(fetchedSettings.duration);
      } catch (error) {
        console.error('Error fetching idle timeout settings:', error);
        setError('Failed to load idle timeout settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle toggle change
  const handleToggleChange = async (event) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const enabled = event.target.checked;
      await IdleTimeoutService.updateEnabledSetting(enabled);
      
      setSettings({ ...settings, enabled });
      setSuccess(true);
    } catch (error) {
      console.error('Error updating idle timeout enabled setting:', error);
      setError('Failed to update idle timeout setting');
    } finally {
      setSaving(false);
    }
  };

  // Save duration
  const handleSaveDuration = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      await IdleTimeoutService.updateDurationSetting(tempDuration);
      
      setSettings({ ...settings, duration: tempDuration });
      setSuccess(true);
    } catch (error) {
      console.error('Error updating idle timeout duration setting:', error);
      setError('Failed to update idle timeout duration');
    } finally {
      setSaving(false);
    }
  };

  // Handle duration change
  const handleDurationChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0 && value <= 60) {
      setTempDuration(value);
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Automatic Logout
          </Typography>
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Typography variant="h6" gutterBottom>
          Automatic Logout
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          When enabled, users will be automatically logged out after a period of inactivity.
          This enhances security by reducing the risk of unauthorized access to unattended sessions.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings updated successfully
          </Alert>
        )}
        
        <Box display="flex" alignItems="center" mt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled}
                onChange={handleToggleChange}
                disabled={saving}
                color="primary"
              />
            }
            label={`Automatic logout is ${settings.enabled ? 'enabled' : 'disabled'}`}
          />
          {saving && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Box>

        {settings.enabled && (
          <Box mt={3}>
            <Box display="flex" alignItems="center" mt={1}>
              <TextField
                label="Duration"
                value={tempDuration}
                onChange={handleDurationChange}
                disabled={saving}
                type="number"
                inputProps={{ min: 1, max: 60 }}
                variant="outlined"
                size="small"
                sx={{ width: '100px' }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                minutes
              </Typography>
            </Box>
            
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveDuration}
                disabled={saving || tempDuration === settings.duration}
                sx={{ mt: 1 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default IdleTimeoutModule;
